import { useState, useRef } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import confetti from 'canvas-confetti';

const API_URL = import.meta.env.VITE_API_URL || '';
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'; // Test key

function Verify() {
  const [step, setStep] = useState('challenge'); // challenge, verify, success
  const [domain, setDomain] = useState('');
  const [agentName, setAgentName] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const [verificationResult, setVerificationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('badge');
  const turnstileRef = useRef(null);

  const handleChallenge = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/v1/challenge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: domain.trim(), agentName: agentName.trim() || null })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate challenge');
      }

      setChallenge(data);
      setStep('verify');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');

    if (!captchaToken) {
      setError('Please complete the CAPTCHA');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/v1/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domain: domain.trim(),
          captchaToken
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      setVerificationResult(data);
      setStep('success');

      // Trigger confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#ff6b6b', '#fa5252', '#c94a3a']
      });
    } catch (err) {
      setError(err.message);
      // Reset turnstile on error
      turnstileRef.current?.reset();
      setCaptchaToken('');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const getBadgeCode = () => {
    const badgeUrl = `${window.location.origin}/v1/badge/${domain}`;
    const profileUrl = `${window.location.origin}/check?domain=${domain}`;
    return `<a href="${profileUrl}" target="_blank" rel="noopener">
  <img src="${badgeUrl}" alt="ClawShield Verified" />
</a>`;
  };

  const getMarkdownBadge = () => {
    const badgeUrl = `${window.location.origin}/v1/badge/${domain}`;
    const profileUrl = `${window.location.origin}/check?domain=${domain}`;
    return `[![ClawShield Verified](${badgeUrl})](${profileUrl})`;
  };

  return (
    <div className="py-12">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Verify Your Agent</h1>
          <p className="text-gray-600">
            Prove ownership of your domain to join the ClawShield registry
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-10">
          {['challenge', 'verify', 'success'].map((s, i) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                  step === s
                    ? 'bg-primary-500 text-white'
                    : ['verify', 'success'].indexOf(step) > ['challenge', 'verify', 'success'].indexOf(s) - 1
                    ? 'bg-primary-100 text-primary-600'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {i + 1}
              </div>
              {i < 2 && (
                <div
                  className={`w-20 h-1 ${
                    ['verify', 'success'].indexOf(step) > i ? 'bg-primary-500' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Step 1: Challenge */}
        {step === 'challenge' && (
          <div className="card p-8">
            <h2 className="text-xl font-semibold mb-6">Step 1: Enter Your Agent Details</h2>
            <form onSubmit={handleChallenge}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Agent Name
                </label>
                <input
                  type="text"
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  placeholder="My AI Assistant"
                  className="input"
                />
                <p className="text-sm text-gray-500 mt-2">
                  The display name for your AI agent
                </p>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Agent Domain
                </label>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="myagent.example.com"
                  className="input"
                  required
                />
                <p className="text-sm text-gray-500 mt-2">
                  The domain where your AI agent is hosted
                </p>
              </div>
              <button type="submit" className="btn-primary w-full" disabled={loading}>
                {loading ? 'Generating...' : 'Get Challenge Token'}
              </button>
            </form>
          </div>
        )}

        {/* Step 2: Verify */}
        {step === 'verify' && challenge && (
          <div className="card p-8">
            <h2 className="text-xl font-semibold mb-6">Step 2: Prove Ownership</h2>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 mb-2">Your verification token:</p>
              <div className="flex items-center">
                <code className="flex-1 bg-white border rounded px-3 py-2 font-mono text-sm break-all">
                  {challenge.token}
                </code>
                <button
                  onClick={() => copyToClipboard(challenge.token)}
                  className="ml-2 p-2 text-gray-500 hover:text-gray-700"
                  title="Copy token"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              <p className="font-medium text-gray-900">Choose one verification method:</p>

              {/* Method 1 */}
              <details className="border rounded-lg">
                <summary className="px-4 py-3 cursor-pointer font-medium text-gray-700 hover:bg-gray-50">
                  Option 1: ClawShield Standard (Recommended)
                </summary>
                <div className="px-4 py-3 border-t bg-gray-50">
                  <p className="text-sm text-gray-600 mb-2">
                    Create a file at <code className="bg-white px-1 rounded">{challenge.instructions.method1.path}</code>
                  </p>
                  <pre className="bg-white border rounded p-3 text-sm overflow-x-auto">
{JSON.stringify({
  ...challenge.instructions.method1.content,
  name: agentName || challenge.instructions.method1.content.name
}, null, 2)}
                  </pre>
                </div>
              </details>

              {/* Method 2 */}
              <details className="border rounded-lg">
                <summary className="px-4 py-3 cursor-pointer font-medium text-gray-700 hover:bg-gray-50">
                  Option 2: Spring AI Standard
                </summary>
                <div className="px-4 py-3 border-t bg-gray-50">
                  <p className="text-sm text-gray-600 mb-2">
                    Create a file at <code className="bg-white px-1 rounded">{challenge.instructions.method2.path}</code>
                  </p>
                  <pre className="bg-white border rounded p-3 text-sm overflow-x-auto">
{JSON.stringify({
  ...challenge.instructions.method2.content,
  name: agentName || challenge.instructions.method2.content.name
}, null, 2)}
                  </pre>
                </div>
              </details>

              {/* Method 3 */}
              <details className="border rounded-lg">
                <summary className="px-4 py-3 cursor-pointer font-medium text-gray-700 hover:bg-gray-50">
                  Option 3: DNS TXT Record
                </summary>
                <div className="px-4 py-3 border-t bg-gray-50">
                  <p className="text-sm text-gray-600 mb-2">Add this TXT record to your DNS:</p>
                  <div className="bg-white border rounded p-3 text-sm">
                    <p><strong>Record:</strong> {challenge.instructions.method3.record}</p>
                    <p><strong>Value:</strong> {challenge.instructions.method3.value}</p>
                  </div>
                </div>
              </details>
            </div>

            <form onSubmit={handleVerify}>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Complete CAPTCHA to verify
                </label>
                <div className="flex justify-center">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={setCaptchaToken}
                    onError={() => setError('CAPTCHA failed. Please try again.')}
                    onExpire={() => setCaptchaToken('')}
                  />
                </div>
              </div>
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setStep('challenge')}
                  className="btn-secondary flex-1"
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1"
                  disabled={loading || !captchaToken}
                >
                  {loading ? 'Verifying...' : 'Verify Agent'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 'success' && verificationResult && (
          <div className="card p-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Verified!</h2>
              <p className="text-gray-600">
                <strong>{agentName || verificationResult.domain}</strong> is now verified on ClawShield
              </p>
            </div>

            {/* Tabs */}
            <div className="border-b mb-6">
              <div className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('badge')}
                  className={`pb-3 text-sm font-medium ${activeTab === 'badge' ? 'tab-active' : 'tab-inactive'}`}
                >
                  Embeddable Badge
                </button>
                <button
                  onClick={() => setActiveTab('qr')}
                  className={`pb-3 text-sm font-medium ${activeTab === 'qr' ? 'tab-active' : 'tab-inactive'}`}
                >
                  QR Code
                </button>
              </div>
            </div>

            {/* Badge Tab */}
            {activeTab === 'badge' && (
              <div>
                <div className="flex justify-center mb-6">
                  <img
                    src={`${API_URL}/v1/badge/${verificationResult.domain}`}
                    alt="ClawShield Badge"
                  />
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">HTML</label>
                    <div className="relative">
                      <pre className="bg-gray-100 rounded p-3 text-sm overflow-x-auto">
                        {getBadgeCode()}
                      </pre>
                      <button
                        onClick={() => copyToClipboard(getBadgeCode())}
                        className="absolute top-2 right-2 p-1.5 bg-white rounded shadow hover:bg-gray-50"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Markdown</label>
                    <div className="relative">
                      <pre className="bg-gray-100 rounded p-3 text-sm overflow-x-auto">
                        {getMarkdownBadge()}
                      </pre>
                      <button
                        onClick={() => copyToClipboard(getMarkdownBadge())}
                        className="absolute top-2 right-2 p-1.5 bg-white rounded shadow hover:bg-gray-50"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* QR Tab */}
            {activeTab === 'qr' && (
              <div className="text-center">
                <div className="inline-block bg-white p-4 rounded-lg shadow mb-4">
                  <img
                    src={`${API_URL}/v1/qr/${verificationResult.domain}`}
                    alt="QR Code"
                    className="w-48 h-48"
                  />
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Scan to view the verification profile
                </p>
                <a
                  href={`${API_URL}/v1/qr/${verificationResult.domain}`}
                  download={`clawshield-${verificationResult.domain}.png`}
                  className="btn-primary inline-block"
                >
                  Download QR Code
                </a>
              </div>
            )}

            {/* Actions */}
            <div className="mt-8 pt-6 border-t flex justify-center space-x-4">
              <a
                href={`/check?domain=${verificationResult.domain}`}
                className="btn-secondary"
              >
                View Profile
              </a>
              <button
                onClick={() => {
                  setStep('challenge');
                  setDomain('');
                  setAgentName('');
                  setChallenge(null);
                  setVerificationResult(null);
                  setCaptchaToken('');
                }}
                className="btn-primary"
              >
                Verify Another Agent
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Verify;
